import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { EnvironmentToken } from '../../app.config';
import {
  TimeGroupBy,
  VisitStatistics,
} from '../models/visit-statistics.model';

@Injectable({
  providedIn: 'root',
})
export class VisitStatisticsService {
  #http = inject(HttpClient);
  #apiUrl = inject(EnvironmentToken).apiUrl;

  #data = signal<VisitStatistics | null>(null);
  #loading = signal(false);

  readonly data = this.#data.asReadonly();
  readonly loading = this.#loading.asReadonly();

  readonly popularRoutes = computed(() => this.#data()?.popular_routes ?? []);
  readonly avgRoutesPerVisit = computed(
    () => this.#data()?.avg_routes_per_visit ?? 0,
  );
  readonly totalVisits = computed(() => this.#data()?.total_visits ?? 0);
  readonly avgTimePerRoute = computed(
    () => this.#data()?.avg_time_per_route ?? [],
  );
  readonly runsTimeline = computed(() => this.#data()?.runs_timeline ?? []);
  readonly visitorsTimeline = computed(
    () => this.#data()?.visitors_timeline ?? [],
  );
  readonly runsPerHour = computed(() => this.#data()?.runs_per_hour ?? []);

  load(
    timeFrom: Date | null,
    timeTo: Date | null,
    runsGroupBy: TimeGroupBy = 'day',
    visitorsGroupBy: TimeGroupBy = 'day',
  ) {
    this.#loading.set(true);

    let params = new HttpParams()
      .set('runs_group_by', runsGroupBy)
      .set('visitors_group_by', visitorsGroupBy);

    if (timeFrom) {
      const start = new Date(timeFrom);
      start.setHours(0, 0, 0, 0);
      params = params.set('time_from', start.toISOString());
    }
    if (timeTo) {
      const end = new Date(timeTo);
      end.setHours(23, 59, 59, 999);
      params = params.set('time_to', end.toISOString());
    }

    this.#http
      .get<{ data: VisitStatistics }>(
        `${this.#apiUrl}/api/visit-statistics`,
        { params },
      )
      .subscribe({
        next: (result) => {
          this.#data.set(result.data);
          this.#loading.set(false);
        },
        error: () => {
          this.#loading.set(false);
        },
      });
  }
}
